import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "@mui/material";
import { StrucRow } from "../StructureElements/StrucRow";
import { StrucCol } from "../StructureElements/StrucCol";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";
import { CustomTextArea } from "../Input/CustomInput";
import { SecondaryButton, WarnButton } from "../Buttons/SecondaryButton";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import LoadingElement from "../loading-element/loading-element";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { useErrorToast } from "../Toast/Toast";

interface DialogCont {
  component: JSX.Element | null;
  open: () => void;
}

type Option = "delete" | "note" | "edit";

// "Fix it myself" (flag REACT_APP_LOGO_REJECT_AND_EDIT_ENABLED): the dialog
// gets the third option only when the caller hands it onRejectAndEdit - the
// caller decides (flag on, the dealer, a logo the editor can open). Without it
// the dialog is today's, byte for byte - the remark field stays UNcontrolled
// as today (the hook passes no reason), so a note typed and then cancelled
// behaves exactly as before. The option has NO remark field (Nichlas 17/10) -
// the callback always receives "".
export function useRejectLogoDialog(
  title: string,
  onReject: (reason?: string) => void,
  onDelete: (reason?: string) => void,
  onClose: () => void,
  isForOrders?: boolean,
  onRejectAndEdit?: (reason: string) => void,
  // Flag logoRejectAndEditNavEnabled (decided by the caller): the logo is
  // already committed to the supplier, so "Fix it myself" keeps the SAME
  // item number and the changes go to the supplier system - the option
  // carries a second hint saying so. Absent = today's dialog.
  rejectAndEditNavHint?: boolean
): DialogCont {
  const { t } = useTranslation();
  const [rejectionNote, setRejectionNote] = useState<string>("");
  const dialog = useDialog();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [option, setOption] = useState<Option>("note");

  function handleReject() {
    // Validate that rejection note is provided when "note" option is selected
    if (option === "note" && !rejectionNote.trim()) {
      useErrorToast(t("compDialogsInput.rejectionNoteRequired"));
      return;
    }

    if (option === "note") {
      onReject(rejectionNote);
    } else if (option === "edit") {
      // No note for "Fix it myself" (Nichlas 17/10: the dealer fixes it
      // themselves - the field is not shown; the API stores a fixed internal
      // note for the blank one). A note typed under "Reject with remark"
      // before switching never travels.
      onRejectAndEdit?.("");
    } else {
      onDelete(rejectionNote);
    }
    dialog.closeDialog();
  }

  function handleCancel() {
    onClose();
    dialog.closeDialog();
  }

  function handleOption(option: Option) {
    setOption(option);
  }

  return {
    component: isLoading ? null : (
      <RejectLogoDialog
        option={option}
        setOption={handleOption}
        isLoading={isLoading}
        dialogVm={dialog}
        reason={onRejectAndEdit ? rejectionNote : undefined}
        setReason={setRejectionNote}
        onReject={handleReject}
        onClose={handleCancel}
        title={title}
        isForOrders={isForOrders}
        canRejectAndEdit={!!onRejectAndEdit}
        rejectAndEditNavHint={rejectAndEditNavHint}
      />
    ),
    open: dialog.openDialog,
  };
}

interface Props {
  option: Option;
  setOption: (option: Option) => void;
  setReason: (reason: string) => void;
  // The note as typed; when given the field is controlled, so a note typed
  // under "Reject with remark" is still there after a visit to "Fix it
  // myself" (where the field is not rendered). The hook passes it ONLY with
  // onRejectAndEdit (flag on); absent = today's uncontrolled field.
  reason?: string;
  onReject: () => void;
  onClose: () => void;
  title: string;
  dialogVm: Dialog_VM;
  isLoading: boolean;
  isForOrders?: boolean;
  // Flag logoRejectAndEditEnabled (decided by the caller): show the third
  // option "Fix it myself". Absent/false = today's dialog.
  canRejectAndEdit?: boolean;
  // Flag logoRejectAndEditNavEnabled (decided by the caller): a committed NAV
  // logo - the "Fix it myself" option shows a second hint ("The item number
  // stays the same ..."). Absent/false = today's hint only.
  rejectAndEditNavHint?: boolean;
}

export function RejectLogoDialog({
  option,
  setOption,
  isLoading,
  reason,
  setReason,
  onReject,
  onClose,
  title,
  dialogVm,
  isForOrders,
  canRejectAndEdit,
  rejectAndEditNavHint,
}: Props) {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const isCustomer =
    profile?.userOrganizationInformations.role === Role.EndUser;
  if (isLoading) return null;

  return (
    <DialogFrame onClose={onClose} dialogOption={dialogVm}>
      {isLoading && <LoadingElement transparent />}
      <span>{title}</span>
      {!isForOrders && (
        <div className={"flex flex-col ml-[-8px]"}>
          {!isCustomer && (
            <div>
              <Radio
                checked={option === "delete"}
                onChange={() => setOption("delete")}
                value="delete"
                name="radio-buttons"
                inputProps={{ "aria-label": t("delete") }}
              />
              <span>{t("delete")}</span>
            </div>
          )}
          <div>
            {!isCustomer && (
              <Radio
                checked={option === "note"}
                onChange={() => setOption("note")}
                value="note"
                name="radio-buttons"
                inputProps={{ "aria-label": t("compDialogsInput.note") }}
              />
            )}
            <span>{t("compDialogsInput.rejectWithRemark")}</span>
          </div>
          {/* Flag logoRejectAndEditEnabled: "Fix it myself" for the dealer
              (never the customer, never orders) - the logo is rejected exactly
              as "Reject with remark" and the editor opens on it (method /
              colours / size). No remark field for this option (Nichlas 17/10);
              "Reject with remark" still requires a note. No e-mail is sent. */}
          {!isCustomer && canRejectAndEdit && (
            <div>
              <Radio
                checked={option === "edit"}
                onChange={() => setOption("edit")}
                value="edit"
                name="radio-buttons"
                inputProps={{ "aria-label": t("compDialogsInput.rejectAndEdit") }}
              />
              <span>{t("compDialogsInput.rejectAndEdit")}</span>
              {option === "edit" && (
                <p className="text-xs text-gray-500 pl-4">
                  {t("compDialogsInput.rejectAndEditHint")}
                </p>
              )}
              {/* Flag logoRejectAndEditNavEnabled: a logo already committed
                  to the supplier keeps its item number - the dealer's
                  changes are written to the supplier system by the API.
                  Only with the caller's say-so (a committed NAV logo); the
                  flag-off dialog has no second line. */}
              {option === "edit" && rejectAndEditNavHint && (
                <p
                  className="text-xs text-gray-500 pl-4"
                  data-testid="reject-and-edit-nav-hint"
                >
                  {t("compDialogsInput.rejectAndEditNavHint")}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {/* The remark field (label + textarea) is NOT rendered for "Fix it
          myself" (only selectable with the callback = flag on; otherwise the
          DOM is today's). Disabled for Delete as today. */}
      {option !== "edit" && (
        <StrucRow className="mt-2">
          <StrucCol>
            <InputWrapper>
              <InputLabel>
                <span>{t("compDialogsInput.rejectionNote")}</span>
              </InputLabel>
              <CustomTextArea
                disabled={option !== "note"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>
      )}
      <div className={"flex justify-between space-x-4"}>
        <WarnButton width={"full"} onClick={onReject}>
          {option === "delete"
            ? t("delete")
            : option === "edit"
              ? t("compDialogsInput.rejectAndEditButton")
              : t("compDialogsInput.reject")}
        </WarnButton>
        <SecondaryButton width={"full"} onClick={onClose}>
          {t("cancel")}
        </SecondaryButton>
      </div>
    </DialogFrame>
  );
}
